/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import uuid from 'uuid';
import AppConfig from '../stores/config';
import { ANALYTICS } from '../utils/enums';
import { INFORMATION_FIELD_TYPES, HELP_SUPPORT_LINK, SOCIAL_LINKS } from '../utils/pdpUtils/pdpEnums';
import { analyticsSharedData } from './index';
import { ANALYTICS_CONSTANTS } from '../utils/commonUtil';

const ANALYTICS_ENUM = {
    CLICK: 'click',
    RENDER: 'render',
};

const socialConstants = (actionId) => {
    switch (actionId) {
    case SOCIAL_LINKS.FACEBOOK:
        return 'Facebook';
    case SOCIAL_LINKS.TWITTER:
        return 'Twitter';
    case SOCIAL_LINKS.YOUTUBE:
        return 'You Tube';
    case SOCIAL_LINKS.INSTAGRAM:
        return 'Instagram';
    default:
        return '';
    }
};

const helpAndSupportConstants = (actionId) => {
    switch (actionId) {
    case HELP_SUPPORT_LINK.TROUBLESHOOTING_AND_HELP:
        return 'Troubleshooting & Help';
    case HELP_SUPPORT_LINK.CONTACT_US:
        return 'Contact Us';
    case HELP_SUPPORT_LINK.TUTORIALS:
        return 'Tutorials';
    case HELP_SUPPORT_LINK.COMMUNITY_AND_FORUM:
        return 'Community & Forum';
    default:
        return '';
    }
};

const informationFieldName = (actionId) => {
    switch (actionId) {
    case INFORMATION_FIELD_TYPES.COMPATIBILITY:
        return 'Compatibility';
    case INFORMATION_FIELD_TYPES.LANGUAGES:
        return 'Languages';
    case INFORMATION_FIELD_TYPES.VERSION:
        return 'Version';
    default:
        return '';
    }
};

class PDPAnalytics {
    constructor() {
        this.carouselButtonClick = this.carouselButtonClick.bind(this);
    }

    /*
     * Can be used to update singleton class object for new pdp data.
     * It takes the app object and sectionId (Category on side nav).
     */
    initAnalyticObject(app, sectionId) {
        const {
            sapcode,
            codexVersion,
            platform,
            appFamilyName,
            isIncompatible,
        } = app;

        this.initialAnalyticObj = {
            event: {
                workflow: 'Apps',
                subcategory: `PDP:${sectionId}`,
            },
            content: {
                id: sapcode,
                name: appFamilyName,
                category: platform,
                version: codexVersion,
            },
        };
        if (isIncompatible) {
            this.initialAnalyticObj.event.value = 'Not Compatible';
        }
    }

    /*
     * Send behance card render analytics
     */
    renderBehanceProjects(card, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.RENDER,
                    subtype: 'project',
                    value: card.cardName,
                },
                content: {
                    type: 'Behance Projects',
                },
                ccd: {
                    render_guid: card.renderGuid,
                    foreground_only: 'true',
                },
            },
            { actionId: card.actionId, sectionId },
        );
    }

    /*
     * Add analytics for left and
     * right carousel buttons.
     */
    carouselButtonClick(buttonEvent) {
        const subtype = buttonEvent.type === 'LEFT_BUTTON_CLICKED' ? 'left' : 'right';
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype,
                    guid: uuid.v4(),
                },
                content: {
                    type: 'Behance Projects',
                },
            },
            {},
        );
    }

    /*
     * behance project click analytics
     */
    clickBehanceProject(card, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'project',
                    value: card.ingestId
                        ? card.ingestId
                        : (card.ingestDescription
                            || ((window.appContext && window.appContext.osLanguage === 'en') ? card.cardName : ANALYTICS_CONSTANTS.EN_STR_NOT_AVAILABLE)),
                    guid,
                },
                content: {
                    type: 'Behance Projects',
                    action: card.cardURL,
                },
            },
            { sectionId, actionId: card.actionId },
        );
    }

    /*
     * Behance section view more click
     */
    viewMoreBehanceClick(viewMore, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'view-more',
                    guid,
                },
                content: {
                    type: 'Behance Projects',
                    action: viewMore.url,
                },
            },
            { actionId: viewMore.actionId, sectionId },
        );
    }

    /*
     * Key features view more click
     */
    viewMoreKeyFeaturesClick(viewMoreKeyFeatures, guid) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'view-more',
                    guid,
                },
                content: {
                    type: 'Key Features',
                },
            },
            { actionId: viewMoreKeyFeatures.actionId, sectionId: viewMoreKeyFeatures.sectionId },
        );
    }

    /*
     * Tutorial card render analytics
     */
    renderTutorialCard(tutorialCardConfig, sectionId, renderGuid) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.RENDER,
                    subtype: 'tutorial',
                    value: (tutorialCardConfig.ingestDescription
                                || ((window.appContext && window.appContext.osLanguage === 'en') ? tutorialCardConfig.description : ANALYTICS_CONSTANTS.EN_STR_NOT_AVAILABLE)),
                },
                content: {
                    type: 'Tutorials',
                },
                ccd: {
                    render_guid: renderGuid,
                    foreground_only: 'true',
                },
            },
            { actionId: tutorialCardConfig.actionId, sectionId },
        );
    }

    /*
     * Tutorial card click event
     */
    tutorialCardClick(tutorialCard, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'tutorial',
                    value: tutorialCard.ingestId
                        ? tutorialCard.ingestId
                        : (tutorialCard.ingestDescription || ((window.appContext && window.appContext.osLanguage === 'en') ? tutorialCard.description : ANALYTICS_CONSTANTS.EN_STR_NOT_AVAILABLE)),
                    guid,
                },
                content: {
                    type: 'Tutorials',
                    action: tutorialCard.tutorialURL,
                },
            },
            { sectionId, actionId: tutorialCard.actionId },
        );
    }

    /*
     * Tutorial view more click
     */
    viewMoreTutorialClick(viewMore, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'view-more',
                    guid,
                },
                content: {
                    type: 'Tutorials',
                    action: viewMore.url,
                },
            },
            { actionId: viewMore.actionId, sectionId },
        );
    }

    categoryClick(category, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'categories',
                    value: category.id, // TODO: we will be sending the id only
                    guid,
                },
                content: {
                    type: 'Categories',
                },
            },
            { sectionId, actionId: category.actionId },
        );
    }

    helpLinkClick(helpLink, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'help-support-link',
                    value: helpAndSupportConstants(helpLink.actionId),
                    guid,
                },
                content: {
                    type: 'Help & Support',
                    action: helpLink.url,
                },
            },
            { actionId: helpLink.actionId, sectionId },
        );
    }

    informationClick(infoObj, guid, sectionId, eventValue) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'information',
                    value: `${informationFieldName(infoObj.actionId)}:${eventValue}`,
                    guid,
                },
                content: {
                    type: 'Information',
                    action: infoObj.fieldUrl,
                },
            },
            { actionId: infoObj.actionId, sectionId },
        );
    }

    otherPlatformClick(appObj, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'other-platform',
                    value: appObj.app.platform, // TODO: use proper casing
                    guid,
                },
                content: {
                    type: 'Other Platforms',
                },
            },
            { actionId: appObj.actionId, sectionId },
        );
    }

    /*
     * Event on click on related app
     */
    relatedAppClick(appObj, guid, sectionId) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'related-apps',
                    value: appObj.app.sapcode,
                    guid,
                },
                content: {
                    type: 'Related Apps',
                },
            },
            { actionId: appObj.actionId, sectionId },
        );
    }

    socialLinkClick(socialLink, guid, sectionId) {
        const { actionId } = socialLink;
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'social-app',
                    value: socialConstants(actionId.toLowerCase()),
                    guid,
                },
                content: {
                    type: 'Social',
                    action: socialLink.url,
                },
            },
            { actionId, sectionId },
        );
    }

    backButtonClick(backButton) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'back',
                },
            },
            { actionId: backButton.actionId, sectionId: backButton.sectionId },
        );
    }

    /*
     * Send Action Bar analytics. Send actionId
     * in callback. Controlller will handle
     * event.subtype
     */
    handleActionBarAnalytics(actionConfig, uiViewType, sectionId, guid) {
        const { actionId, progressCount } = actionConfig;
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: actionId,
                    count: progressCount,
                    guid,
                },
                ui: {
                    view_type: uiViewType,
                    filter: 'pdp',
                },
            },
            { actionId, sectionId }
        );
    }

    /*
     * send pdp render event
     */
    pdpPageRenderAnalytic(renderGuid) {
        const event = {
            type: 'render',
            subtype: 'page',
        };
        const ccd = {
            render_guid: renderGuid,
            foreground_only: 'true',
        };
        const { shouldSendLoadTime } = analyticsSharedData;
        this.pdpAnalyticCallback({ event, ccd }, { shouldSendLoadTime });
        analyticsSharedData.shouldSendLoadTime = false;
    }

    /**
     * Send banner/video render analytics from PDP
     */
    bannerRenderEvent(bannerConfig, renderGuid) {
        const subtype = bannerConfig.isVideo ? 'video' : 'banner';
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.RENDER,
                    subtype,
                },
                ui: {
                    view_type: bannerConfig.viewType,
                },
                ccd: {
                    render_guid: renderGuid,
                    foreground_only: 'true',
                },
            }
        );
    }

    /**
     * Send banner cta click events
     */
    bannerCTAClickEvent(bannerConfig) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'banner',
                    value: (bannerConfig.ingestDescription
                            || ((window.appContext && window.appContext.osLanguage === 'en') ? bannerConfig.CTAText : ANALYTICS_CONSTANTS.EN_STR_NOT_AVAILABLE)),
                    guid: bannerConfig.guid,
                },
                ui: {
                    view_type: bannerConfig.viewType,
                },
                content: {
                    action: bannerConfig.url,
                },
            },
            {}
        );
    }

    /**
     * Video play/click event
     */
    playVideoEvent(bannerConfig) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: ANALYTICS_ENUM.CLICK,
                    subtype: 'video',
                },
                ui: {
                    view_type: bannerConfig.viewType,
                },
            },
            {}
        );
    }

    /**
     * Send event when video is closed
     */
    videoCloseEvent(bannerConfig) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: 'close',
                    subtype: 'video',
                },
                ui: {
                    view_type: bannerConfig.viewType,
                },
            },
            {}
        );
    }

    /**
     * Event to be sent on video
     * pause/resume.
     */
    videoControlEvent(bannerConfig) {
        this.pdpAnalyticCallback(
            {
                event: {
                    type: bannerConfig.type,
                    subtype: 'video',
                },
                ui: {
                    view_type: bannerConfig.viewType,
                },
            },
            {}
        );
    }

    pdpAnalyticCallback(eventAnalyticObj, componentParameter = {}) {
        if (!eventAnalyticObj) {
            return;
        }
        const componentParams = componentParameter;
        componentParams.source = ANALYTICS.PDP;
        componentParams.surfaceType = ANALYTICS.SURFACE_TYPE.CATALOG;
        const event = { ...this.initialAnalyticObj.event, ...eventAnalyticObj.event };
        const content = { ...this.initialAnalyticObj.content, ...eventAnalyticObj.content };
        const analyticObjectList = [{ ...eventAnalyticObj, event, content }];
        AppConfig.analyticsCallback({ componentParams, analyticObjectList });
    }
}

export default new PDPAnalytics();
